import {
    AlertListTypeEnum,
    AlertOriginTypesEnum,
    AlertTypes,
    AlertTypesEnum,
    MyAlertListSearchStatusMapping,
} from '../../../utilities/Constants';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import { Stack, Table } from 'react-bootstrap';
import {
    availableSubTypeOptions,
    availableTypeOptions,
    useAlertListFilters,
} from './AlertListFiltersContext';
import { cloneDeep, isEmpty } from 'lodash';

import CheckmateNSelect from '../../../components/shared/CheckmateNSelect';
import { IAlertSearchModel } from '../../../interfaces/INote';
import { LoadingButton } from '../../../shared/buttons/LoadingButton';
import UIHelper from '../../../utilities/UIHelper';
import { showErrorToast } from '../../../shared/ToastUtils';
import { useState } from 'react';

const lookupToOptionMapper = (lookupItem: ILookupModel) =>
    ({
        id: lookupItem.id,
        guid: lookupItem.guid,
        value: lookupItem.id.toString(),
        label: lookupItem.displayName ?? lookupItem.name,
    }) as IMultiSelectOption;

export interface IAlertListFiltersProps {
    alertListType: AlertListTypeEnum;
    appliedSearchModel: IAlertSearchModel;
    isSearching: boolean;
    onApplyFilters: (searchModel: IAlertSearchModel) => void;
}

export function AlertListSearchFilters(props: IAlertListFiltersProps) {
    const alertListFiltersContext = useAlertListFilters();

    const [pendingSearchModel, setPendingSearchModel] = useState<IAlertSearchModel>(
        props.appliedSearchModel
    );

    const getEnabledFilters = ({
        isMyAlertList,
        isZoneAlertList,
    }: {
        isMyAlertList: boolean;
        isZoneAlertList: boolean;
    }) => {
        let enabledFilters: string[] = [];

        const noteTypes = [
            AlertOriginTypesEnum.CaseNote,
            AlertOriginTypesEnum.ZoneNote,
            AlertOriginTypesEnum.StrategyNote,
            AlertOriginTypesEnum.PackageSettlementNote,
        ];

        const onlyOneNoteTypeIsSelected =
            pendingSearchModel.originTypes?.every((id) => noteTypes.includes(id)) &&
            pendingSearchModel.originTypes?.length === 1;

        enabledFilters = ['type'];
        if (isMyAlertList) {
            enabledFilters = ['type', 'myAlertListStatus'];
        } else if (isZoneAlertList) {
            enabledFilters = ['type', 'user', 'noteStatus'];
        }

        const onlyTypeSelectedIsReport =
            pendingSearchModel.originTypes?.length === 1 &&
            pendingSearchModel.originTypes?.[0] === AlertOriginTypesEnum.Report;

        const alertTypeIsAct = pendingSearchModel.alertTypeIds?.some(
            (id) => id === AlertTypes.Act.Value
        );

        if (onlyTypeSelectedIsReport && alertTypeIsAct) {
            enabledFilters.push('subtype');
        }

        if (onlyOneNoteTypeIsSelected) {
            enabledFilters.push('noteCategory');
            enabledFilters.push('purpose');
        }

        if ((pendingSearchModel.noteCategories?.length ?? 0) > 0) {
            enabledFilters.push('noteSubCategory');
        }

        return enabledFilters;
    };

    const getSelectedAlertStatusOptions = () => {
        if (props.alertListType === AlertListTypeEnum.MyAlertList) {
            return (pendingSearchModel.statuses
                ?.map((status) =>
                    MyAlertListSearchStatusMapping.find(
                        (x) => x.alertStatusType === status.status && x.read === status.read
                    )
                )
                .filter((mapping) => mapping !== undefined)
                .map((mapping) =>
                    alertListFiltersContext
                        .getAlertStatusTypeOptions(props.alertListType)
                        .find((opt) => opt.id === mapping?.id)
                ) ?? []) as IMultiSelectOption[];
        } else if (props.alertListType === AlertListTypeEnum.ZoneAlertList) {
            return (
                pendingSearchModel.statuses
                    ?.map((status) =>
                        alertListFiltersContext
                            .getAlertStatusTypeOptions(props.alertListType)
                            .find((x) => x.id === status.status)
                    )
                    .filter((lu) => lu !== undefined)
                    .map((lu) => lookupToOptionMapper(lu!)) ?? []
            );
        }

        return [];
    };

    const validateFilters = (): boolean => {

        const hasType = !isEmpty(pendingSearchModel.originTypes);
        const hasStatus = !isEmpty(pendingSearchModel.statuses);

        if (!hasType && !hasStatus) {
            showErrorToast('Enter a Type and Status filter to search');
            return false;
        } else if(!hasType) {
            showErrorToast('Enter a Type  filter to search');
            return false;
        } else if(!hasStatus) {
            showErrorToast('Enter a Status filter to search');
            return false;
        }
        
        return true;
    };

    const handleApplyFiltersClick = () => {
        if (!validateFilters()) {
            return;
        }

        props.onApplyFilters(pendingSearchModel);
    };

    const handleClearFiltersClick = () => {
        setPendingSearchModel({ alertListType: props.alertListType });
    };

    const handleAlertOriginTypeFilterOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.originTypes = optionsList.map((item) => item.id);

        const originTypesChangesDetected = UIHelper.difference(
            pendingSearchModelCopy.originTypes,
            pendingSearchModelCopy.originTypes
        );

        if (originTypesChangesDetected) {
            pendingSearchModelCopy.noteCategories = [];
            pendingSearchModelCopy.noteSubCategories = [];
        }

        const hasReportAlertOriginType = optionsList.some(
            (opt) => opt.id === AlertOriginTypesEnum.Report
        );

        const hasCaseTriggerAlertOriginType = optionsList.some(
            (opt) => opt.id === AlertOriginTypesEnum.CaseTrigger
        );

        if (hasReportAlertOriginType) {
            pendingSearchModelCopy.alertTypeIds = pendingSearchModelCopy.alertTypeIds?.filter(
                (id) => id !== AlertTypesEnum.Watch
            );
        }

        if (hasCaseTriggerAlertOriginType) {
            pendingSearchModelCopy.alertTypeIds = [];
            pendingSearchModelCopy.alertSubTypeIds = [];
        }

        pendingSearchModelCopy.originTypes = optionsList.map((opt) => opt.id);
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleMyAlertListStatusFilterOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        const statusOptions = alertListFiltersContext.getAlertStatusTypeOptions(
            props.alertListType
        );
        pendingSearchModelCopy.statuses = optionsList
            .map((item) => MyAlertListSearchStatusMapping.find((x) => x.id === item.id) ?? null)
            .filter((status) => status)
            .map((status) => ({
                status: status!.alertStatusType,
                read: status!.read,
            }));

        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleZoneAlertListStatusFilterOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.statuses = optionsList.map((item: IMultiSelectOption) => ({
            status: item.id!,
        }));
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleUserOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.alertUserGuids = optionsList.map(
            (item: IMultiSelectOption) => item.guid!
        );
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleTypeOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.alertTypeIds = optionsList.map(
            (item: IMultiSelectOption) => item.id
        );
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleSubTypeOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.alertSubTypeIds = optionsList.map(
            (item: IMultiSelectOption) => item.id
        );
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleNoteCategoryFilterOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.noteCategories = optionsList.map(
            (item: IMultiSelectOption) => item.id
        );
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handleNoteSubCategoryFilterOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.noteSubCategories = optionsList.map(
            (item: IMultiSelectOption) => item.id
        );
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const handlePurposeFilterOptionsChange = (optionsList: IMultiSelectOption[]) => {
        const pendingSearchModelCopy = cloneDeep(pendingSearchModel);
        pendingSearchModelCopy.purpose = optionsList.map((item: IMultiSelectOption) => item.id);
        setPendingSearchModel(pendingSearchModelCopy);
    };

    const isMyAlertList = props.alertListType === AlertListTypeEnum.MyAlertList;
    const isZoneAlertList = props.alertListType === AlertListTypeEnum.ZoneAlertList;

    const enabledFilters = getEnabledFilters({ isMyAlertList, isZoneAlertList });
    const statusOptions = alertListFiltersContext.getAlertStatusTypeOptions(props.alertListType);

    const isCaseTriggerSelected = pendingSearchModel.originTypes?.some(
        (id) => id === AlertOriginTypesEnum.CaseTrigger
    );

    return (
        <Stack style={{ justifyContent: 'space-between', height: '100%' }}>
            <div style={{ flexGrow: 1 }}>
                <Table borderless className="mt-1">
                    <tbody>
                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Type
                            </td>
                            <td>
                                <CheckmateNSelect
                                    name="alertOriginType"
                                    options={alertListFiltersContext.getAlertOriginTypeOptions(
                                        props.alertListType
                                    )}
                                    value={
                                        alertListFiltersContext
                                            .getAlertOriginTypeOptions(props.alertListType)
                                            .filter((opt) =>
                                                pendingSearchModel.originTypes?.includes(opt.id)
                                            ) ?? []
                                    }
                                    onChange={handleAlertOriginTypeFilterOptionsChange}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Status
                            </td>
                            <td>
                                {isMyAlertList ? (
                                    <CheckmateNSelect
                                        name="myAlertListStatus"
                                        options={statusOptions}
                                        value={getSelectedAlertStatusOptions()}
                                        onChange={handleMyAlertListStatusFilterOptionsChange}
                                        disabled={!enabledFilters.includes('myAlertListStatus')}
                                    />
                                ) : (
                                    <CheckmateNSelect
                                        name="noteStatus"
                                        options={statusOptions}
                                        value={
                                            statusOptions?.filter((opt) =>
                                                pendingSearchModel.statuses
                                                    ?.map((status) => status.status)
                                                    .includes(opt.id)
                                            ) ?? []
                                        }
                                        onChange={handleZoneAlertListStatusFilterOptionsChange}
                                        disabled={!enabledFilters.includes('noteStatus')}
                                    />
                                )}
                            </td>
                        </tr>

                        {isZoneAlertList && (
                            <tr style={{ verticalAlign: 'middle' }}>
                                <td style={{ width: 125 }} className="nowrap">
                                    Alert User
                                </td>
                                <td>
                                    <CheckmateNSelect
                                        name="user"
                                        options={alertListFiltersContext.getSortedAvailableUserOptions()}
                                        value={
                                            alertListFiltersContext
                                                .getSortedAvailableUserOptions()
                                                ?.filter((opt) => {
                                                    return (
                                                        opt.guid &&
                                                        pendingSearchModel.alertUserGuids?.includes(
                                                            opt.guid
                                                        )
                                                    );
                                                }) ?? []
                                        }
                                        onChange={handleUserOptionsChange}
                                        disabled={!enabledFilters.includes('user')}
                                    />
                                </td>
                            </tr>
                        )}

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Act/Watch
                            </td>
                            <td>
                                <CheckmateNSelect
                                    name="type"
                                    options={availableTypeOptions}
                                    value={
                                        availableTypeOptions?.filter((opt) =>
                                            pendingSearchModel.alertTypeIds?.includes(opt.id)
                                        ) ?? []
                                    }
                                    onChange={handleTypeOptionsChange}
                                    disabled={
                                        !enabledFilters.includes('type') || isCaseTriggerSelected
                                    }
                                />
                            </td>
                        </tr>

                        {props.alertListType === AlertListTypeEnum.MyAlertList && (
                            <tr style={{ verticalAlign: 'middle' }}>
                                <td style={{ width: 125 }} className="nowrap">
                                    Act Type
                                </td>
                                <td>
                                    <CheckmateNSelect
                                        name="subType"
                                        options={availableSubTypeOptions}
                                        value={
                                            availableSubTypeOptions?.filter((opt) =>
                                                pendingSearchModel.alertSubTypeIds?.includes(opt.id)
                                            ) ?? []
                                        }
                                        onChange={handleSubTypeOptionsChange}
                                        disabled={
                                            !enabledFilters.includes('subtype') ||
                                            isCaseTriggerSelected
                                        }
                                    />
                                </td>
                            </tr>
                        )}

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Category
                            </td>
                            <td>
                                <CheckmateNSelect
                                    name="noteCategory"
                                    options={alertListFiltersContext.getNoteCategoryTypeOptions(
                                        pendingSearchModel.originTypes ?? []
                                    )}
                                    value={
                                        alertListFiltersContext
                                            .getNoteCategoryTypeOptions(
                                                pendingSearchModel.originTypes ?? []
                                            )
                                            .filter((opt) =>
                                                pendingSearchModel.noteCategories?.includes(opt.id)
                                            ) ?? []
                                    }
                                    onChange={handleNoteCategoryFilterOptionsChange}
                                    disabled={!enabledFilters.includes('noteCategory')}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Sub Category
                            </td>
                            <td>
                                <CheckmateNSelect
                                    name="noteSubCategory"
                                    options={alertListFiltersContext.getNoteSubCategoryTypeOptions(
                                        pendingSearchModel.noteCategories ?? []
                                    )}
                                    value={
                                        alertListFiltersContext
                                            .getNoteSubCategoryTypeOptions(
                                                pendingSearchModel.noteCategories ?? []
                                            )
                                            .filter((opt) =>
                                                pendingSearchModel.noteSubCategories?.includes(
                                                    opt.id
                                                )
                                            ) ?? []
                                    }
                                    onChange={handleNoteSubCategoryFilterOptionsChange}
                                    disabled={!enabledFilters.includes('noteSubCategory')}
                                />
                            </td>
                        </tr>

                        <tr style={{ verticalAlign: 'middle' }}>
                            <td style={{ width: 125 }} className="nowrap">
                                Purpose
                            </td>
                            <td>
                                <CheckmateNSelect
                                    name="purpose"
                                    options={alertListFiltersContext.getNotePurposeTypeOptions()}
                                    value={
                                        alertListFiltersContext
                                            .getNotePurposeTypeOptions()
                                            .filter((opt) =>
                                                pendingSearchModel.purpose?.includes(opt.id)
                                            ) ?? []
                                    }
                                    onChange={handlePurposeFilterOptionsChange}
                                    disabled={!enabledFilters.includes('purpose')}
                                />
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </div>
            <div style={{ flexShrink: 1, paddingTop: '1.5em', borderTop: '1px solid lightgray' }}>
                <LoadingButton
                    buttonCssClass="btn btn-black"
                    onClick={handleApplyFiltersClick}
                    text="Apply Filters"
                    loadingText="Searching..."
                    loading={props.isSearching}
                />
                <button
                    className="btn btn-default"
                    onClick={handleClearFiltersClick}
                    disabled={props.isSearching}
                >
                    Clear Filters
                </button>
            </div>
        </Stack>
    );
}
